import {
  ColumnDirective,
  ColumnsDirective,
  EditSettingsModel,
  GridComponent,
  Inject,
  Page,
  Sort,
  Toolbar,
} from '@syncfusion/ej2-react-grids';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import '../../../../../core/styles/sortable-columns.css';

import { InvoiceRecordPayment } from './InvoiceRecordPayment';
import { InvoiceSend } from './InvoiceSend';

import { CloseButton } from '@components/ActionButtons';
import { Box } from '@components/Box';
import { FormButtonsContainer } from '@components/Container';
import { CancelButton } from '@components/Form';
import { CustomSelectOption } from '@components/Form/CustomSelect';
import { OkButton } from '@components/Form/OkButton';
import { DynamicRecordFilter, TableActions } from '@components/Grid';
import { Loading } from '@components/Loading';
import { Modal } from '@components/Modal';
import { PageContainer } from '@components/PageContainer';
import { routes } from '@config/routes';
import { useGridFilters } from '@hooks/useGridFilters';
import { useGridOperations } from '@hooks/useGridOperations';
import { useNotification } from '@hooks/useNotification';
import { useSyncfusionGrid } from '@hooks/useSyncfusionGrid';
import { InvoiceStatus, invoiceStatusSelectOptions } from '@models/Invoice';
import { InvoiceService } from '@services/Invoice';

export const InvoiceList: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  const [queryFilter, setQueryFilter] = useState<string>('');
  const [showDelete, setShowDelete] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number>(0);
  const [selectedInvoiceDisplayString, setSelectedInvoiceDisplayString] =
    useState<any>(0);
  const [customerFilterSelectOptions, setCustomerFilterSelectOptions] =
    useState<CustomSelectOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCustomerFilterLoading, setIsCustomerFilterLoading] =
    useState<boolean>(true);
  const [showSend, setShowSend] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);

  // Create a service wrapper that matches useSyncfusionGrid interface
  const invoiceServiceWrapper = {
    ...InvoiceService,
    getList: async (
      page: number,
      take: number | undefined,
      sortBy: string | undefined,
      sortDirection: string | undefined,
      filters: string
    ) => {
      return InvoiceService.getList(
        page,
        take,
        undefined,
        undefined,
        filters,
        sortBy,
        sortDirection
      );
    },
  };

  const {
    gridData,
    gridRef,
    gridKey,
    renderComplete,
    dataStateChange,
    fetchData,
  } = useSyncfusionGrid({
    service: invoiceServiceWrapper,
    queryFilter,
    defaultSortBy: 'ID',
    defaultSortDirection: 'desc',
  });

  // Custom hooks for grid functionality
  const {
    filters,
    updateFilter,
    updateFilterAndApply,
    clearFilters,
    applyFilters,
  } = useGridFilters(
    {
      customerFilter: '',
      statusFilter: '',
      dateFromFilter: '',
      dateToFilter: '',
      invoiceNumberFilter: '',
    },
    setQueryFilter
  );

  const {
    handleDelete: handleDeleteOperation,
    handleVoid: handleVoidOperation,
    handlePost: handlePostOperation,
    handlePosted: handlePostedOperation,
  } = useGridOperations({
    service: InvoiceService,
    refreshGrid: fetchData,
    entityName: 'invoice',
  });

  const editOptions: EditSettingsModel = {
    allowAdding: true,
    allowDeleting: true,
    allowEditing: true,
    mode: 'Dialog',
  };

  const loadCustomerFilterSelectOptions = () => {
    InvoiceService.getCustomerFilterSelectOptions().then((result) => {
      if (result.isSuccess && 'data' in result) {
        const customerSelectOptions = result.data.map((row) => ({
          value: row.value,
          text: row.text,
        }));
        setCustomerFilterSelectOptions(customerSelectOptions);
        setIsCustomerFilterLoading(false);
      } else {
        const errorMessage = `Failed to get customer filter select options. ${result.errorMessages}`;
        console.error(errorMessage);
        showError(errorMessage);
      }
    });
  };

  useEffect(() => {
    loadCustomerFilterSelectOptions();
  }, []);

  useEffect(() => {
    if (!isCustomerFilterLoading) {
      setIsLoading(false);
    }
  }, [isCustomerFilterLoading]);

  const confirmDelete = () => {
    handleDeleteOperation(selectedInvoiceId, selectedInvoiceDisplayString);
    setShowDelete(false);
  };

  const handleSend = async (rowId: number, rowInvoiceNumber: string) => {
    try {
      setSelectedInvoiceId(rowId);
      setSelectedInvoiceDisplayString(rowInvoiceNumber);
      setShowSend(true);
    } catch (error) {
      const errorMessage = `Failed to handle send action for invoice ${rowInvoiceNumber}.`;
      console.error(errorMessage);
      showError(errorMessage);
    }
  };

  const handleRecordPayment = async (
    rowId: number,
    rowInvoiceNumber: string
  ) => {
    try {
      setSelectedInvoiceId(rowId);
      setSelectedInvoiceDisplayString(rowInvoiceNumber);
      setShowRecordPayment(true);
    } catch (error) {
      const errorMessage = `Failed to handle record payment action for invoice ${rowInvoiceNumber}.`;
      console.error(errorMessage);
      showError(errorMessage);
    }
  };

  const handleDelete = async (rowId: number, rowInvoiceNumber: string) => {
    try {
      setSelectedInvoiceId(rowId);
      setSelectedInvoiceDisplayString(rowInvoiceNumber);
      setShowDelete(true);
    } catch (error) {
      const errorMessage = `Failed to handle delete action for invoice ${rowInvoiceNumber}.`;
      console.error(errorMessage);
      showError(errorMessage);
    }
  };

  const handleView = (rowId: number) => {
    console.log('View', rowId);
    navigate(`${routes.RNC_INVOICES}/${rowId}`);
  };

  const handleEdit = (rowId: number) => {
    navigate(`${routes.RNC_INVOICES}/${rowId}/edit`);
  };

  const handleExportToPdf = (rowId: number) => {
    console.log('Export to PDF', rowId);
    navigate(`${routes.RNC_INVOICES}/${rowId}/pdf`);
  };

  const handleDuplicate = (rowId: number) => {
    InvoiceService.duplicate(rowId).then((result) => {
      if (result.isSuccess) {
        fetchData(true);
        const successMessage = `Successfully duplicated the invoice record.`;
        console.log(successMessage);
        showSuccess(successMessage);
      } else {
        const errorMessage = `Failed to duplicate the invoice record. ${result.errorMessages}`;
        showError(errorMessage);
        console.error(errorMessage);
      }
    });
  };

  const actionButtons = (props: any) => {
    return (
      <TableActions
        dropdownActions={[
          {
            title: 'Edit',
            onClick: () => handleEdit(props.id),
            isHidden:
              props.status === InvoiceStatus.Posted ||
              props.status === InvoiceStatus.Voided,
          },
          {
            title: 'View',
            onClick: () => handleView(props.id),
          },
          {
            title: 'Send',
            onClick: () => handleSend(props.id, props.invoiceNumber),
          },
          {
            title: 'Duplicate',
            onClick: () => handleDuplicate(props.id),
          },
          {
            title: 'Post',
            onClick: () => {
              handlePostOperation(props.id);
            },
            isHidden: props.status !== InvoiceStatus.Draft,
          },
          {
            title: 'Send Invoice',
            onClick: () => handleSend(props.id, props.invoiceNumber),
            isHidden:
              props.status === InvoiceStatus.Draft ||
              props.status === InvoiceStatus.Posted ||
              props.status === InvoiceStatus.Voided,
          },
          {
            title: 'Posted',
            onClick: () => {
              handlePostedOperation(props.id);
            },
            isHidden:
              props.status === InvoiceStatus.Draft ||
              (props.status === InvoiceStatus.Post && props.Balance > 0) ||
              (props.status === InvoiceStatus.EmailSent && props.Balance > 0) ||
              props.status === InvoiceStatus.Posted ||
              props.status === InvoiceStatus.Voided,
          },
          {
            title: 'Void',
            onClick: () => {
              handleVoidOperation(props.id);
            },
          },

          {
            title: 'Record Payment',
            onClick: () => handleRecordPayment(props.id, props.invoiceNumber),
            isHidden:
              props.status === InvoiceStatus.Draft ||
              (props.status === InvoiceStatus.Post && props.Balance === 0) ||
              (props.status === InvoiceStatus.EmailSent &&
                props.Balance === 0) ||
              props.status === InvoiceStatus.Posted ||
              props.status === InvoiceStatus.Voided,
          },

          {
            title: 'Export to PDF',
            onClick: () => handleExportToPdf(props.id),
          },
          {
            showTopSeparator: true,
            title: 'Delete',
            onClick: () => handleDelete(props.id, props.invoiceNumber),
          },
        ]}
        // menuOnTop={menuOnTop}
      />
    );
  };

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <>
        <DynamicRecordFilter
          label="Filters"
          filters={[
            {
              label: 'Select Customer',
              type: 'select',
              selection: customerFilterSelectOptions,
              value: filters.customerFilter,
              onChange: (e) => {
                updateFilterAndApply('customerFilter', e.target.value, () =>
                  fetchData(true)
                );
              },
            },
            {
              label: 'Select Status',
              type: 'select',
              selection: invoiceStatusSelectOptions,
              value: filters.statusFilter,
              onChange: (e) => {
                updateFilterAndApply('statusFilter', e.target.value, () =>
                  fetchData(true)
                );
              },
            },
            {
              label: 'Date From',
              type: 'date',
              value: filters.dateFromFilter,
              onChange: (e) => {
                updateFilterAndApply('dateFromFilter', e.target.value, () =>
                  fetchData(true)
                );
              },
            },
            {
              label: 'Date To',
              type: 'date',
              value: filters.dateToFilter,
              onChange: (e) => {
                updateFilterAndApply('dateToFilter', e.target.value, () =>
                  fetchData(true)
                );
              },
            },
            {
              label: 'Invoice Number',
              type: 'text',
              value: filters.invoiceNumberFilter,
              onChange: (e) => {
                updateFilter('invoiceNumberFilter', e.target.value);
              },
            },
          ]}
          onFilter={() => {
            applyFilters(() => fetchData(true));
          }}
          onClear={() => {
            clearFilters();
            applyFilters(() => fetchData(true));
          }}
        />
        <PageContainer>
          <GridComponent
            key={gridKey}
            dataBound={renderComplete}
            dataSource={gridData}
            ref={gridRef}
            allowPaging={true}
            allowSorting={true}
            pageSettings={{
              pageSize: 10,
              pageSizes: [5, 10, 20, 50, 100],
            }}
            editSettings={editOptions}
            dataStateChange={dataStateChange}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="statusDisplayString"
                headerText="Status"
                customAttributes={{ class: 'sortable-column' }}
              />
              <ColumnDirective
                field="invoiceNumber"
                headerText="Number"
                customAttributes={{ class: 'sortable-column' }}
              />
              <ColumnDirective
                field="customerCompanyName"
                headerText="Customer"
                customAttributes={{ class: 'sortable-column' }}
              />
              <ColumnDirective
                field="total"
                headerText="Gross Amount"
                customAttributes={{ class: 'sortable-column' }}
                template={(props: any) => {
                  return (
                    <span>
                      {Number(props.total).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  );
                }}
              />
              <ColumnDirective
                field="balance"
                headerText="Balance"
                customAttributes={{ class: 'sortable-column' }}
                template={(props: any) => {
                  return (
                    <span>
                      {Number(props.balance).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  );
                }}
              />
              <ColumnDirective
                headerText="Actions"
                width={140}
                allowSorting={false}
                customAttributes={{ class: 'non-sortable-column' }}
                template={actionButtons}
              />
            </ColumnsDirective>
            <Inject services={[Page, Sort, Toolbar]} />
          </GridComponent>

          {showDelete && (
            <Modal show={true}>
              <CloseButton onClick={() => setShowDelete(false)} />
              <Box size="medium">
                <div className="px-4 pb-2">
                  <span>
                    Are you sure you want to delete Invoice:{' '}
                    {selectedInvoiceDisplayString}
                  </span>
                </div>
                <FormButtonsContainer>
                  <div className="space-x-2">
                    <CancelButton onClick={() => setShowDelete(false)} />
                    <OkButton onClick={confirmDelete}>Delete</OkButton>
                  </div>
                </FormButtonsContainer>
              </Box>
            </Modal>
          )}

          {showSend && (
            <Modal show={true}>
              <InvoiceSend
                onDownload={() => handleExportToPdf(selectedInvoiceId)}
                invoiceId={selectedInvoiceId}
                onClose={(refreshGrid?: boolean) => {
                  setShowSend(false);
                  if (refreshGrid) {
                    fetchData(true);
                  }
                }}
                invoiceName={selectedInvoiceDisplayString}
              />
            </Modal>
          )}

          {showRecordPayment && (
            <Modal show={true}>
              <InvoiceRecordPayment
                invoiceId={selectedInvoiceId}
                onClose={(refreshGrid?: boolean) => {
                  setShowRecordPayment(false);
                  if (refreshGrid) {
                    fetchData(true);
                  }
                }}
              />
            </Modal>
          )}
        </PageContainer>
      </>
    );
  }
};
